import './FieldRangeCaret.css'
import { CaretDownIcon, CaretUpIcon } from '../icons'
import { useRef, useCallback, useEffect } from 'react'
import { NumericFormat } from 'react-number-format'

interface FieldRangeCaretProps {
  value: number | undefined
  onChange: (value: number | undefined) => void
  controls?: {
    min?: number
    max?: number
    step: number
  }
  disabled?: boolean
  className?: string
}

/**
 * Custom hook to handle press-and-hold button behavior
 * @param action - The function to execute on hold
 * @param holdDelay - Delay before continuous execution starts (ms)
 * @param holdInterval - Interval between executions during hold (ms)
 * @returns Handlers for mouse/touch events
 */
function useHoldButton(action: () => void, holdDelay: number = 500, holdInterval: number = 100) {
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const holdIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const clearTimers = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current)
      holdTimeoutRef.current = null
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current)
      holdIntervalRef.current = null
    }
  }, [])

  const startHold = useCallback(() => {
    action()

    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        action()
      }, holdInterval)
    }, holdDelay)
  }, [action, holdDelay, holdInterval])

  const stopHold = useCallback(() => {
    clearTimers()
  }, [clearTimers])

  useEffect(() => {
    return () => clearTimers()
  }, [clearTimers])

  return {
    onMouseDown: startHold,
    onMouseUp: stopHold,
    onMouseLeave: stopHold,
    onTouchStart: startHold,
    onTouchEnd: stopHold
  }
}

export default function FieldRangeCaret({
  value,
  onChange,
  controls = {
    min: undefined,
    max: undefined,
    step: 1
  },
  disabled = false,
  className = ''
}: FieldRangeCaretProps) {
  const { min, max, step } = controls

  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  const stepRef = useRef(step)
  const minRef = useRef(min)
  const maxRef = useRef(max)

  useEffect(() => {
    valueRef.current = value
    onChangeRef.current = onChange
    stepRef.current = step
    minRef.current = min
    maxRef.current = max
  }, [value, onChange, step, min, max])

  const handleDecrement = useCallback(() => {
    const currentValue = valueRef.current ?? 0
    const newValue = currentValue - stepRef.current
    if (minRef.current !== undefined && newValue < minRef.current) {
      return
    }
    onChangeRef.current(newValue)
  }, [])

  const handleIncrement = useCallback(() => {
    const currentValue = valueRef.current ?? 0
    const newValue = currentValue + stepRef.current
    if (maxRef.current !== undefined && newValue > maxRef.current) {
      return
    }
    onChangeRef.current(newValue)
  }, [])

  const decrementHoldHandlers = useHoldButton(handleDecrement)
  const incrementHoldHandlers = useHoldButton(handleIncrement)

  const isDecrementDisabled = disabled || (min !== undefined && value !== undefined && value <= min)
  const isIncrementDisabled = disabled || (max !== undefined && value !== undefined && value >= max)

  const getDecimalScale = () => {
    const stepStr = step.toString()
    const decimalIndex = stepStr.indexOf('.')
    return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1
  }

  const decimalScale = getDecimalScale()

  return (
    <div
      className={`field-range-caret ${disabled ? 'field-range-caret--disabled' : ''} ${className}`}
    >
      <NumericFormat
        value={value}
        onValueChange={values => {
          const numValue = values.floatValue
          onChange(numValue)
        }}
        isAllowed={values => {
          const numValue = values.floatValue
          if (numValue === undefined) {
            return true
          }
          if (min !== undefined && numValue < min) {
            return false
          }
          if (max !== undefined && numValue > max) {
            return false
          }
          return true
        }}
        disabled={disabled}
        allowNegative={min === undefined || min < 0}
        decimalScale={decimalScale}
        className="field-range-caret-input"
        displayType="input"
        thousandSeparator={false}
      />

      <div className="field-range-caret-buttons">
        <button
          type="button"
          {...incrementHoldHandlers}
          disabled={isIncrementDisabled}
          className="field-range-caret-button field-range-caret-increment"
          aria-label="Increment"
        >
          <CaretUpIcon className="field-range-caret-icon" />
        </button>
        <button
          type="button"
          {...decrementHoldHandlers}
          disabled={isDecrementDisabled}
          className="field-range-caret-button field-range-caret-decrement"
          aria-label="Decrement"
        >
          <CaretDownIcon className="field-range-caret-icon" />
        </button>
      </div>
    </div>
  )
}
